const { Debouncer } = require("../utilities/debouncer");

const updateTracker = {};

const STATUS_MESSAGES = {
  MISSING_ID: "Missing update context. Please try again or refresh the page.",
  INVALID_UPDATE: "Invalid update. Please try again or refresh the page.",
  MISSING_UPDATE_DATA: "Missing data for update. Please try again.",
  TRYING_AGAIN: "Save failed. Trying again...",
  SAVING_FAILED: "Saving attempts failed.",
};

/**
 * ELYXUpdater is an abstraction that handles updating data in
 * a common pattern that frequently started occurring.
 *
 * This prevents having to set up similar large chunks of logic
 * every single time, for managing updates, queing updates, delaying
 * updates, managing the success and fail states of updates. Setting
 * this logic up in one place allows perfecting it here, while leaving
 * room for middleware stages  a scenarios require more flexibility.
 *
 * All of a sudden, all that rich functionality is available by just
 * calling a simple function.
 *
 * It also allows for a standardized structure for CRUD functions,
 * which increases readability and overall workflow due to code
 * increased degrees of code predictability, given you always know
 * which functions are expected and make up a full update.
 *
 * Requires passing in
 * @param {Object} updateObject
 * @param {String} updateObject.ID Unique identifier of the data being updated
 * @param {Function} updateObject.update API function that makes the actual update request
 * @param {Object} updateObject.updateData Data to pass into the API update function
 * @param {Function} updateObject.onSuccess Function to call in case of a successful update
 * @param {Function} updateObject.onFail Function to call in case of a failed update
 * @param {Boolean} updateObject.delayed Whether to delay the update
 * @param {Number} updateObject.delay Optional delay threshold
 */
async function ELYXUpdater(updateObject = {}) {
  /** Ensure all data exists and is correct for the update object */
  validateUpdateObject(updateObject);

  /** Ensure an update with such an ID is being tracked */
  ensureUpdateTrackerIsInitialized(updateObject.ID);

  const tracker = updateTracker[updateObject.ID];

  /** Check if update is meant to be debounced and not executed immediately */
  if (updateObject.delayed) {
    const earliestAllowedUpdateMS = Date.now();
    const threshold = updateObject.delay ?? 2 * 1000; // default threshold is 2 seconds
    await tracker.debounce(earliestAllowedUpdateMS, threshold, async () => await performUpdate(updateObject));
    return;
  }

  /** If saving is still in progress from a previous update for the same ID,
   * queue the update to prevent race conditions. Likewise if saving is in
   * a failed state, queue the update to wait for the problem to resolve so
   * that all updates would occur in the right order to prevent data overrides. */
  if (tracker.savingInProgress || tracker.savingFailed) {
    tracker.savingQueue.push(updateObject);
    return;
  }

  /** Make the actual update */
  await performUpdate(updateObject);
}

function initNewUpdateTracker(ID) {
  updateTracker[ID] = {
    recentlyChangedFields: {},
    savingInProgress: false,
    savingFailed: false,
    savingAttemps: 0,
    savingQueue: [],
    debounce: Debouncer(),
  };
}

export function checkFieldsHaveChanged(ID, field) {
  /** If no tracker exists, no fields have changed */
  if (!Object.hasOwn(updateTracker, ID)) {
    return false;
  }

  /** Extract correct tracker for re-use */
  const tracker = updateTracker[ID];

  /** If field does not exist, no fields have changed */
  if (!Object.hasOwn(tracker.recentlyChangedFields, field)) {
    return false;
  }

  /** If somehow field still exists but is empty, delete it
   * and return that no fields have changed */
  if (!tracker.recentlyChangedFields[field].length) {
    delete tracker.recentlyChangedFields[field];
    return false;
  }

  /** Return that fields HAVE changed */
  return true;
}

export function syncRecentlyChangedField(ID, field) {
  /** If no tracker exists, cancel sync */
  if (!Object.hasOwn(updateTracker, ID)) {
    return;
  }

  const stamp = Date.now();

  /** Add recently changed field with timestamp */
  const tracker = updateTracker[ID];
  if (!Object.hasOwn(tracker.recentlyChangedFields, field)) {
    tracker.recentlyChangedFields[field] = [stamp];
  } else {
    tracker.recentlyChangedFields[field].push(stamp);
  }
}

function clearRecentlyChangedFields(updateObject) {
  const { ID, field, updateTimeMS } = updateObject;

  /** If any of the fields missing, reject update */
  if (!ID || !field || !updateTimeMS) {
    rejectELYXUpdate(STATUS_MESSAGES.MISSING_UPDATE_DATA);
  }

  /** If no tracker exists, cancel clearing */
  if (!Object.hasOwn(updateTracker, ID)) {
    return;
  }

  /** Extract correct tracker for re-use */
  const tracker = updateTracker[ID];

  /** If field does not exist, cancel clearing */
  if (!Object.hasOwn(tracker.recentlyChangedFields, field)) {
    return;
  }

  /** Clear stamps that have occurred earlier or at the same time as the update
   * @NB Loop starting from end as upon splicing, index position of each date
   * would change. */
  for (var i = tracker.recentlyChangedFields[field].length - 1; i > -1; i--) {
    const stampMS = tracker.recentlyChangedFields[field][i];
    if (stampMS <= updateTimeMS) {
      tracker.recentlyChangedFields[field].splice(i, 1);
    }
  }

  /** If no stamps are left, delete the recently changed field to mark that
   * the data is now in sync */
  if (!tracker.recentlyChangedFields[field].length) {
    delete tracker.recentlyChangedFields[field];
  }
}

export function ensureUpdateTrackerIsInitialized(ID) {
  if (!Object.hasOwn(updateTracker, ID)) {
    initNewUpdateTracker(ID);
  }
}

function validateUpdateObject(obj = {}) {
  if (!obj.ID) {
    rejectELYXUpdate(STATUS_MESSAGES.MISSING_ID);
  }
  if (!obj.update || obj.update instanceof Function !== true) {
    rejectELYXUpdate(STATUS_MESSAGES.INVALID_UPDATE);
  }
  if (!obj.updateData || !Object.keys(obj.updateData).length) {
    rejectELYXUpdate(STATUS_MESSAGES.MISSING_UPDATE_DATA);
  }
}

/**
 * Rejects an update and displays an error message to the user.
 * @param {String} reason
 */
function rejectELYXUpdate(reason) {
  console.log(reason);
  throw new Error(reason);
}

function displayUpdateStatusMessage(message) {
  console.log(message);
}

/**
 * An update needs to handle the following:
 * - Perform the actual update
 * - Manage successful and failed scenarios of updates
 * - Manage saving states (whether in progress or failed)
 * - Manage a saving queue to prevent simultaneous updates
 * @param {Object} updateObject
 */
async function performUpdate(updateObject) {
  /** Reject if tracker for item no longer exists, such as for a queued update */
  if (!Object.hasOwn(updateTracker, updateObject.ID)) {
    return;
  }

  /** Don't update if no fields have changed */
  if (!checkFieldsHaveChanged(updateObject.ID, updateObject.field)) {
    return;
  }

  /** Create reference for the specific tracker */
  const tracker = updateTracker[updateObject.ID];

  /** Clear recent changes matching this update */
  clearRecentlyChangedFields(updateObject);

  /** Set updater for ID as saving */
  tracker.savingInProgress = true;

  /** Make actual request */
  const response = await updateObject.update(updateObject.updateData);

  /** Handle a successfully executed update request */
  if (response && response.success) {
    /** If saving had previously been in a failed state, undo that */
    tracker.savingFailed = false;

    /** Reset saving attempts counter */
    tracker.savingAttempts = 0;

    /** Execute success scenario function, if one was provided */
    if (updateObject.onSuccess) {
      await updateObject.onSuccess(response.data ?? {});
    }
  } else {
    /** If the saving failed, set the updateTracker state to a failed on */
    tracker.savingFailed = true;
    tracker.savingAttemps++;

    /** Execute failed scenario function, if one was provided */
    if (updateObject.onFail) {
      await updateObject.onFail(response.data ?? {});
    }

    /** Add the update to the start of the queue to try it again until
     * maximum attempts are reached (to prevent infinite loop). */
    if (tracker.savingAttemps < 5) {
      displayUpdateStatusMessage(STATUS_MESSAGES.TRYING_AGAIN);
      tracker.savingQueue.unshift(updateObject);
    } else {
      displayUpdateStatusMessage(STATUS_MESSAGES.SAVING_FAILED);
    }
  }

  /** Unset updater for ID as saving, since the request has finished */
  tracker.savingInProgress = false;

  /** Take care of any queued updates */
  await handleQueuedUpdates(updateObject.ID);

  /** @todo Mark to be removed from tracker ??? */
}

async function handleQueuedUpdates(ID) {
  if (Object.hasOwn(updateTracker, ID)) {
    const tracker = updateTracker[ID];
    if (tracker.savingQueue.length) {
      /** Extract the first update in the list */
      const updateObject = tracker.savingQueue[0];

      /** Remove the update from the list */
      tracker.savingQueue.shift();

      /** Perform the extracted queued update */
      await performUpdate(updateObject);
    }
  }
}

export default ELYXUpdater;
